// react
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

// types
import {
	ConversionPayload,
	Entity,
	ErrorCause,
	ExchangeRateByDate,
	Feedback,
	FinancierExt,
	LoadingStatus,
	Loan,
	Payment,
	Transaction,
	TransactionType,
} from '../../../@types';
import { financiersExt } from '../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';
import { useExchangeRates } from '../../../hooks/useExchangeRates';

// mui
import { Alert, AlertTitle, Box, Button, Grid, SxProps, Tooltip, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// fields
import { Checkbox } from '../../fields/Checkbox/Checkbox';
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { Input, InputProps } from '../../fields/Input/Input';

// forms
import { FormSearch } from '../../forms/FormSearch/FormSearch';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BtnFilters } from '../../ui/BtnFilters/BtnFilters';
import { ChipReimbursement } from '../../ui/ChipReimbursement/ChipReimbursement';
import { IconCircle, transactionIconTypes } from '../../ui/IconCircle/IconCircle';
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';
import { Table, TableHeader } from '../../ui/Table/Table';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';

// icons
import { Add as IconAdd, DeleteOutlined as IconDelete } from '@mui/icons-material';

// theme
import { theme } from '../../../theme';

// utils
import { formatDate } from '../../../utils/dates';
import { translateTransactionType } from '../../../utils/translations';
import { calcTransactionTotalByCurrency } from '../../../utils/transactions';
import { formatCurrency } from '../../../utils/currency';

// Row type that joins a payment with its parent transaction for display
interface PaymentRow {
	payment: Payment;
	transaction: Transaction;
}

// props
export interface DialogUpdateTransactionTransactionsFilters {
	account: string;
	category: string;
	component: string;
	dateEnd: string;
	dateStart: string;
	tags: string[];
	withdrawalApplication: string;
}

export interface DialogUpdateTransactionTransactionsProps {
	DialogActionsProps?: Partial<DialogActionsProps>;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	transaction: Transaction;
}

export type ValuesProps = Record<string, string | boolean>;

export interface BuildConversionValuesParams {
	amount: number;
	baseCurrency: string;
	paymentCurrency: string;
	paymentId: number | string;
	rates: ExchangeRateByDate[];
}

const buildConversionValues = ({
	amount,
	baseCurrency,
	paymentCurrency,
	paymentId,
	rates,
}: BuildConversionValuesParams): Record<string, string> => {
	const entries: Record<string, string> = {};

	// payment currency is in rates when payment is different from base currency
	const paymentCurrencyRate = rates.find((r) => r.currency === paymentCurrency);
	const baseAmount = paymentCurrencyRate?.exchangeRate
		? amount * Number(paymentCurrencyRate.exchangeRate)
		: amount;

	for (const er of rates) {
		if (er.currency === paymentCurrency) {
			// Key by the base currency
			entries[`${paymentId}-conversion-${baseCurrency}`] = baseAmount.toFixed(2);
		} else if (er.exchangeRate) {
			// Key by target currency
			entries[`${paymentId}-conversion-${er.currency}`] = (
				baseAmount / Number(er.exchangeRate)
			).toFixed(2);
		}
	}
	return entries;
};

export const DialogUpdateTransactionTransactions = ({
	DialogActionsProps = {},
	isOpen,
	onClose,
	onSuccess,
	transaction,
}: DialogUpdateTransactionTransactionsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// context
	const { setBanner } = useContext(ContextBanner);

	// router
	const [searchParams] = useSearchParams();

	// id for the selected-transactions form
	const formId = 'form-update-transaction-transactions';

	// params
	const paramAccount = searchParams.get('dialogAccount');
	const paramCategory = searchParams.get('dialogCategory');
	const paramComponent = searchParams.get('dialogComponent');
	const paramDateEnd = searchParams.get('dialogDateEnd');
	const paramDateStart = searchParams.get('dialogDateStart');
	const paramQ = searchParams.get('dialogQ');
	const paramTagsString = searchParams.get('dialogTags');
	const paramTags = useMemo(
		() => (paramTagsString ? [...new Set(paramTagsString.split(',').filter(Boolean))] : []),
		[paramTagsString]
	);
	const paramWithdrawalApplication = searchParams.get('dialogWithdrawalApplication');

	// filters from URL params
	const filtersFromParams = useMemo(() => {
		return {
			account: paramAccount || '',
			category: paramCategory || '',
			component: paramComponent || '',
			dateEnd: paramDateEnd || '',
			dateStart: paramDateStart || '',
			tags: paramTags || [],
			withdrawalApplication: paramWithdrawalApplication || '',
		};
	}, [
		paramAccount,
		paramCategory,
		paramComponent,
		paramDateEnd,
		paramDateStart,
		paramTags,
		paramWithdrawalApplication,
	]);

	// defaults
	const reimbursementPayments = Array.isArray(transaction.payments)
		? transaction.payments.filter((p) => typeof p === 'object')
		: [];
	const currency = reimbursementPayments.length > 0 ? reimbursementPayments[0].currency : 'USD';
	const currenciesForExchangeRates = useMemo(
		() => getCurrenciesForExchangeRates({ currenciesExtra: [currency], currency }),
		[currency, getCurrenciesForExchangeRates]
	);

	const exchangeRatesDefault = useMemo(() => {
		return currenciesForExchangeRates.map((curr) => ({
			currency: curr,
			dateExchangeRate: '',
			exchangeRate: 0,
		}));
	}, [currenciesForExchangeRates]);

	// refs
	const refExchangeRatesForm = useRef<HTMLFormElement>(null);

	// state
	const [exchangeRates, setExchangeRates] = useState<ExchangeRateByDate[]>(exchangeRatesDefault);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [rowsAvailable, setRowsAvailable] = useState<PaymentRow[]>([]);
	const [rowsSelected, setRowsSelected] = useState<PaymentRow[]>([]);
	const [status, setStatus] = useState<LoadingStatus>('DEFAULT');
	const [transactionsApplied, setTransactionsApplied] = useState<Transaction[]>([]);
	const [values, setValues] = useState<ValuesProps>({
		hasAllConversions: false,
	});

	const flattenToRows = (transactions: Transaction[]): PaymentRow[] => {
		const rows: PaymentRow[] = [];
		for (const txn of transactions) {
			for (const payment of txn.payments) {
				if (typeof payment !== 'object') continue;
				const loan = payment.loan as Loan;
				if (!financiersExt.includes(loan?.financier as FinancierExt)) continue;
				rows.push({ payment, transaction: txn });
			}
		}
		return rows;
	};

	const buildQueryString = () => {
		const params = new URLSearchParams();
		params.append('project', String(transaction.project));
		params.append('type', 'CASH_BENEFIT,OPEX,PAYMENT');
		params.append('financier', 'GOV,OTH');
		params.append(
			'populate',
			'fromEntity,payments,payments.conversions,payments.loan,payments.reimbursed,payments.reimbursed.conversions,toEntity'
		);
		params.append(
			'fields',
			'currency,date,fromEntity,id,payments,payments.conversions,payments.reimbursed,payments.reimbursed.conversions,toEntity,type,withdrawalApplication'
		);

		if (searchParams.get('dialogQ')) params.append('q', searchParams.get('dialogQ') || '');
		if (filtersFromParams.dateStart) params.append('dateStart', filtersFromParams.dateStart);
		if (filtersFromParams.dateEnd) params.append('dateEnd', filtersFromParams.dateEnd);
		if (filtersFromParams.account) params.append('account', filtersFromParams.account);
		if (filtersFromParams.category) params.append('category', filtersFromParams.category);
		if (filtersFromParams.component) params.append('component', filtersFromParams.component);
		if (filtersFromParams.tags.length > 0) params.append('tags', filtersFromParams.tags.join(','));
		if (filtersFromParams.withdrawalApplication)
			params.append('withdrawalApplication', filtersFromParams.withdrawalApplication);

		return params.toString();
	};

	// fetch already-applied transactions
	const { isLoading: isLoadingRelatedTransactions } = useFetch({
		isEnabled: isOpen && Boolean(transaction.transactions?.length),
		url: `${process.env.REACT_APP_API_URL}/transactions?id=${transaction.transactions?.join(',')}&populate=fromEntity,payments,payments.conversions,payments.loan,payments.reimbursed,payments.reimbursed.conversions,toEntity&fields=currency,date,fromEntity,id,payments,payments.conversions,payments.reimbursed,payments.reimbursed.conversions,toEntity,type`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const rows = flattenToRows(res.data);
				setRowsSelected(rows);

				const valuesNew: ValuesProps = {};
				const ratesFromConversions = new Map<string, ExchangeRateByDate>();

				// Seed amount and conversion values from stored reimbursed amounts
				rows.forEach(({ payment }) => {
					if (payment.reimbursed) {
						// Set reimbursed amount
						if (payment.reimbursed.amount) {
							valuesNew[`amount-${payment.id}`] = String(payment.reimbursed.amount);
						}

						// Populate conversion values from stored reimbursed conversions
						for (const conversion of payment.reimbursed.conversions) {
							// Set conversion amount for this currency
							valuesNew[`${payment.id}-conversion-${conversion.currency}`] = String(
								conversion.amount
							);

							// Seed exchange rates from stored conversion data
							const seedCurrency =
								conversion.currency === currency ? payment.currency : conversion.currency;

							if (!ratesFromConversions.has(seedCurrency)) {
								const dateExchangeRate = formatDate({
									date: conversion.dateExchangeRate,
									format: 'YYYY-MM-DD',
								});
								ratesFromConversions.set(seedCurrency, {
									currency: seedCurrency,
									dateExchangeRate,
									exchangeRate: conversion.exchangeRate ?? 0,
								});
							}
						}
					}
				});

				setTransactionsApplied(res.data);
				setValues((valuesOld) => {
					return {
						...valuesOld,
						...valuesNew,
					};
				});

				// Merge rates from conversions into exchangeRates
				if (ratesFromConversions.size > 0) {
					setExchangeRates((exchangeRatesPrev) => {
						const prevMap = new Map(exchangeRatesPrev.map((er) => [er.currency, er]));
						const exchangeRatesNew = exchangeRatesPrev.map(
							(er) => ratesFromConversions.get(er.currency) || er
						);

						for (const [curr, rate] of ratesFromConversions) {
							if (!prevMap.has(curr)) {
								exchangeRatesNew.push(rate);
							}
						}
						return exchangeRatesNew;
					});
				}
			}
		},
	});

	// fetch eligible transactions
	const { fetchRequest: fetchTransactions, isLoading: isLoadingTransactions } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/transactions?${buildQueryString()}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onError: () => {
			setRowsAvailable([]);
		},
		onSuccess: (res) => {
			if (res.data) {
				const selectedPaymentIds = new Set(rowsSelected.map((r) => r.payment.id));
				const newRows = flattenToRows(res.data).filter(
					(r) => !selectedPaymentIds.has(r.payment.id)
				);
				setRowsAvailable(newRows);
			} else {
				setRowsAvailable([]);
			}
		},
	});

	// totals
	const totalSelected = rowsSelected.reduce((sum, { payment }) => {
		const amountKey =
			payment.currency === currency
				? `amount-${payment.id}`
				: `${payment.id}-conversion-${currency}`;
		const val = Number(values[amountKey]);
		return sum + (isNaN(val) ? 0 : val);
	}, 0);
	const totalReimbursement = calcTransactionTotalByCurrency({ transaction }) || 0;

	// styles
	const sxBox = {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 2,
	};
	const sxBoxHeading = {
		backgroundColor: theme.palette.brand.grey[100],
		padding: theme.spacing(2),
	};
	const sxBoxHeader = {
		...sxBoxHeading,
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
	};
	const sxBoxFooter = {
		...sxBoxHeading,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	};
	const sxTableBox = {
		maxHeight: 380,
		overflowY: 'auto',
	};

	const asterisk = (
		<>
			<Typography color="error" component="span">
				*
			</Typography>
		</>
	);

	// base columns shared by both tables
	const columnsBase: TableHeader[] = [
		{
			value: 'type',
			label: t('type'),
			render: (_: unknown, header: TableHeader, row: PaymentRow) => {
				const { transaction: txn } = row;
				return (
					<Grid alignItems="center" container spacing={1} wrap="nowrap">
						<Grid item>
							<IconCircle size="sm" type={transactionIconTypes[txn.type]} />
						</Grid>
						<Grid item>
							<Grid container direction="column">
								<Grid item>
									<TypographyCaption id={txn.id} />
								</Grid>
								<Grid item>
									<Typography>
										{translateTransactionType(txn.type as TransactionType, t)}
									</Typography>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			},
		},
		{
			value: 'date',
			label: t('date'),
			render: (_: unknown, header: TableHeader, row: PaymentRow) => (
				<Time date={row.transaction.date} />
			),
		},
		{
			value: 'entity',
			label: t('entity'),
			render: (_: unknown, header: TableHeader, row: PaymentRow) => {
				const { transaction: txn } = row;
				return (
					<Box sx={{ maxWidth: 100, overflow: 'hidden' }}>
						<LinkEntity
							entity={((txn.toEntity || txn.fromEntity) as Entity) || undefined}
							hasEllipsis={true}
						/>
					</Box>
				);
			},
		},
		{
			value: 'financing',
			label: t('financing'),
			render: (_: unknown, header: TableHeader, row: PaymentRow) => {
				const loan = row.payment.loan as Loan;
				return <Typography>{loan?.code}</Typography>;
			},
		},
		{
			value: 'eligible',
			label: t('eligible'),
			render: (_: unknown, header: TableHeader, row: PaymentRow) => (
				<Amount currency={row.payment.currency} value={row.payment.amount} />
			),
		},
	];

	const headersFind: TableHeader[] = [
		...columnsBase,
		{
			align: 'right',
			value: 'add',
			label: '',
			render: (_: unknown, header: TableHeader, row: PaymentRow) => {
				const isApplied = transactionsApplied.find((t) => t.id === row.transaction.id);
				const btn = (
					<Button
						disabled={row.transaction.hasReimbursement && !isApplied}
						onClick={() => handleAddRow(row)}
						size="small"
						variant="contained">
						<IconAdd />
					</Button>
				);

				if (row.transaction.hasReimbursement && !isApplied) {
					return (
						<Tooltip arrow={true} placement="top" title={t('alreadyReimbursed')}>
							<span>{btn}</span>
						</Tooltip>
					);
				}

				return btn;
			},
		},
	];

	const headersSelected: TableHeader[] = [
		...columnsBase,
		{
			value: 'amount',
			label: t('amount'),
			render: (_: unknown, header: TableHeader, row: PaymentRow) => {
				const { payment } = row;

				const currenciesConversion = [
					// include base currency if payment uses different currency
					...(payment.currency !== currency ? [currency] : []),
					// include exchange rate currencies except the payment currency itself
					...exchangeRates
						.filter((er) => er.currency !== payment.currency)
						.map((er) => er.currency),
				];

				const inputProps: Partial<InputProps> = {
					inputProps: { min: 0, step: '0.01' },
					required: true,
					size: 'small',
					type: 'number',
				};

				let currenciesHidden: string[] = [];

				// hide if currency is not the base currency or the payment currency
				if (!values.hasAllConversions) {
					currenciesHidden = currenciesConversion.filter(
						(c) => c !== currency && c !== payment.currency
					);
				}

				let sxContainer: SxProps = {};

				// if no conversion currencies are shown
				if (currenciesConversion.length - currenciesHidden.length === 0) {
					sxContainer = {
						...sxContainer,
						marginTop: 0,
					};
				}

				return (
					<Grid alignItems="center" container spacing={1} sx={sxContainer}>
						<Grid item xs={12}>
							<Input
								{...inputProps}
								currency={payment.currency}
								onChange={(e) => {
									const amountNew = Number(e.target.value);
									setValues((valuesPrev) => ({
										...valuesPrev,
										[`amount-${payment.id}`]: e.target.value,
										...buildConversionValues({
											amount: amountNew,
											baseCurrency: currency,
											paymentCurrency: payment.currency,
											paymentId: payment.id,
											rates: exchangeRates,
										}),
									}));
								}}
								name={`amount-${payment.id}`}
								value={values[`amount-${payment.id}`]}
							/>
						</Grid>
						{currenciesConversion.map((c) => {
							const name = `${payment.id}-conversion-${c}`;
							let display = 'flex';

							if (currenciesHidden.includes(c)) {
								display = 'none';
							}

							return (
								<Grid item key={name} xs={12}>
									<Input
										{...inputProps}
										currency={c}
										InputProps={{ readOnly: true }}
										name={name}
										sx={{ display }}
										value={values[name] || ''}
									/>
								</Grid>
							);
						})}
					</Grid>
				);
			},
		},
		{
			value: 'remove',
			label: '',
			render: (_: unknown, header: TableHeader, row: PaymentRow) => (
				<Button color="error" onClick={() => handleRemoveRow(row)} size="small" variant="outlined">
					<IconDelete />
				</Button>
			),
		},
	];

	// handlers
	const handleAddRow = (row: PaymentRow) => {
		setFeedback(null);
		setRowsSelected([...rowsSelected, row]);
		setRowsAvailable(rowsAvailable.filter((r) => r.payment.id !== row.payment.id));

		// prefill amount input for added row if not already set
		// prioritizing reimbursed amount if available
		if (!(`amount-${row.payment.id}` in values)) {
			let amount = row.payment.amount;

			if (row.payment.reimbursed && row.payment.reimbursed?.amount !== null) {
				amount = row.payment.reimbursed.amount;
			}

			setValues((valuesPrev) => ({
				...valuesPrev,
				[`amount-${row.payment.id}`]: String(amount),
				...buildConversionValues({
					amount: Number(amount),
					baseCurrency: currency,
					paymentCurrency: row.payment.currency,
					paymentId: row.payment.id,
					rates: exchangeRates,
				}),
			}));
		}
	};

	const handleAddAll = () => {
		setFeedback(null);
		const valuesNew: ValuesProps = { ...values };
		rowsAvailable.forEach(({ payment }) => {
			if (!(`amount-${payment.id}` in valuesNew)) {
				let amount = payment.amount;

				if (payment.reimbursed && payment.reimbursed?.amount !== null) {
					amount = payment.reimbursed.amount;
				}

				valuesNew[`amount-${payment.id}`] = String(amount);
				Object.assign(
					valuesNew,
					buildConversionValues({
						amount: Number(amount),
						baseCurrency: currency,
						paymentCurrency: payment.currency,
						paymentId: payment.id,
						rates: exchangeRates,
					})
				);
			}
		});
		setValues((valuesPrev) => {
			return {
				...valuesPrev,
				...valuesNew,
			};
		});
		setRowsSelected([...rowsSelected, ...rowsAvailable]);
		setRowsAvailable([]);
	};

	const handleRemoveRow = (row: PaymentRow) => {
		setFeedback(null);
		setRowsSelected(rowsSelected.filter((r) => r.payment.id !== row.payment.id));
		setRowsAvailable([...rowsAvailable, row]);
	};

	const handleClear = () => {
		setFeedback(null);
		setRowsAvailable([...rowsAvailable, ...rowsSelected]);
		setRowsSelected([]);
	};

	const handleChangeExchangeRates = (index: number, name: string, value: string | number) => {
		const exchangeRatesNew = [...exchangeRates];
		exchangeRatesNew[index] = { ...exchangeRatesNew[index], [name]: value };
		setExchangeRates(exchangeRatesNew);

		// recompute conversion values for all selected payments
		const valuesNew: ValuesProps = {};
		for (const { payment } of rowsSelected) {
			const amount = Number(values[`amount-${payment.id}`]);
			Object.assign(
				valuesNew,
				buildConversionValues({
					amount,
					baseCurrency: currency,
					paymentCurrency: payment.currency,
					paymentId: payment.id,
					rates: exchangeRatesNew,
				})
			);
		}

		if (Object.keys(valuesNew).length) {
			setValues((valuesPrev) => {
				return {
					...valuesPrev,
					...valuesNew,
				};
			});
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setFeedback(null);

		// Validate exchange rates if selected rows exist
		if (rowsSelected.length) {
			if (refExchangeRatesForm.current && !refExchangeRatesForm.current.reportValidity()) {
				return;
			}
		}

		setStatus('LOADING');

		try {
			// group selected rows by transaction id
			const txnMap = new Map<number, { transaction: Transaction; payments: PaymentRow[] }>();

			for (const row of rowsSelected) {
				const txnId = row.transaction.id;
				if (txnMap.has(txnId)) {
					txnMap.get(txnId)?.payments.push(row);
				} else {
					txnMap.set(txnId, { transaction: row.transaction, payments: [row] });
				}
			}

			const transactionsPayload = Array.from(txnMap.values()).map(
				({ transaction: txn, payments }) => ({
					id: txn.id,
					payments: payments.map(({ payment }) => {
						const amount = Number(values[`amount-${payment.id}`]);
						const conversions: ConversionPayload[] = [];

						// Add base currency conversion when payment is not using base currency
						if (payment.currency !== currency) {
							const baseKey = `${payment.id}-conversion-${currency}`;
							const paymentCurrencyRate = exchangeRates.find(
								(er) => er.currency === payment.currency
							);
							if (values[baseKey] && paymentCurrencyRate) {
								conversions.push({
									amount: Number(values[baseKey]),
									currency,
									dateExchangeRate: paymentCurrencyRate.dateExchangeRate,
									exchangeRate: paymentCurrencyRate.exchangeRate,
									tax: 0,
								});
							}
						}

						// All rates except the payment currency
						for (const er of exchangeRates) {
							if (er.currency !== payment.currency) {
								const key = `${payment.id}-conversion-${er.currency}`;
								if (values[key]) {
									conversions.push({
										amount: Number(values[key]),
										currency: er.currency,
										dateExchangeRate: er.dateExchangeRate,
										exchangeRate: er.exchangeRate,
										tax: 0,
									});
								}
							}
						}

						return {
							id: payment.id,
							reimbursed: {
								amount,
								conversions,
								currency: payment.currency,
							},
						};
					}),
				})
			);

			const reqPatchTransactions = await fetch(
				`${process.env.REACT_APP_API_URL}/transactions/${transaction.id}`,
				{
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						'Content-Type': 'application/json',
						User: String(idToken),
					},
					body: JSON.stringify({ transactions: transactionsPayload }),
				}
			);

			if (!reqPatchTransactions.ok) {
				throw new Error('Failed to update transaction');
			}

			const resPatchTransactions = await reqPatchTransactions.json();

			if (resPatchTransactions.error) {
				throw new Error(resPatchTransactions.error.message, {
					cause: {
						details: resPatchTransactions.error.details,
						id: resPatchTransactions.error.id,
					},
				});
			}

			setStatus('SUCCESS');

			if (onSuccess) {
				onSuccess();
			}

			setBanner({
				hasClose: true,
				message: t('alert.transactionsApplied'),
				severity: 'success',
			});

			onClose();
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause?.id === 'TRANSACTION_REIMBURSED_EXCEEDS_REIMBURSEMENT') {
				message = t('error.reimbursementExceeded', {
					totalReimbursed: formatCurrency({ currency, value: cause.details.totalReimbursed }),
					totalReimbursement: formatCurrency({ currency, value: totalReimbursement }),
				});
			}

			setFeedback({ alert: { children: message, severity: 'error' } });
			setStatus('ERROR');
		}
	};

	// fetch transactions when dialog opens or filters change
	useEffect(() => {
		if (isOpen) {
			if (
				paramAccount ||
				paramCategory ||
				paramComponent ||
				paramDateEnd ||
				paramDateStart ||
				paramQ ||
				paramTags.length ||
				paramWithdrawalApplication
			) {
				fetchTransactions();
			} else {
				setRowsAvailable([]);
			}
		}
	}, [
		fetchTransactions,
		isOpen,
		paramAccount,
		paramCategory,
		paramComponent,
		paramDateEnd,
		paramDateStart,
		paramQ,
		paramTags,
		paramWithdrawalApplication,
	]);

	// Update exchangeRates when rowsSelected changes
	// Always include project currencies (currenciesForExchangeRates)
	// Include currencies from selected payments, excluding the base currency
	// Remove currencies no longer needed unless they are project currencies
	useEffect(() => {
		const projectCurrencySet = new Set(currenciesForExchangeRates);
		const selectedCurrencies = rowsSelected.map((r) => r.payment.currency);

		// Union of required currencies, excluding base currency since it doesn't need an exchange rate
		const requiredCurrencies = [...new Set([...projectCurrencySet, ...selectedCurrencies])].filter(
			(c) => c !== currency
		);

		setExchangeRates((exchangeRatesPrev) => {
			const prevMap = new Map(exchangeRatesPrev.map((er) => [er.currency, er]));

			const exchangeRatesNew = requiredCurrencies.map(
				(curr) => prevMap.get(curr) || { currency: curr, dateExchangeRate: '', exchangeRate: 0 }
			);

			// Skip update if the currency set is unchanged
			const currenciesPrev = exchangeRatesPrev
				.map((er) => er.currency)
				.sort()
				.join(',');
			const currenciesNew = exchangeRatesNew
				.map((er) => er.currency)
				.sort()
				.join(',');

			if (currenciesPrev === currenciesNew) {
				return exchangeRatesPrev;
			}

			return exchangeRatesNew;
		});
	}, [currency, currenciesForExchangeRates, rowsSelected]);

	// reset state when dialog closes
	useEffect(() => {
		if (!isOpen) {
			setFeedback(null);
			setStatus('DEFAULT');
			setRowsAvailable([]);
			setRowsSelected([]);
			setExchangeRates(exchangeRatesDefault);
			setValues({});
		}
	}, [exchangeRatesDefault, isOpen]);

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: onClose,
				},
				{
					children: t('applyTransactions'),
					form: formId,
					loading: status === 'LOADING',
					type: 'submit',
					variant: 'contained',
				},
			]}
			hasClose={true}
			isOpen={isOpen}
			maxWidth={900}
			onClose={onClose}
			title={{
				children: t('applyTransactions'),
				variant: 'h3',
			}}
			{...DialogActionsProps}>
			<Grid container spacing={3}>
				{/* Exchange Rates */}
				{exchangeRates.length > 0 && (
					<Grid item xs={12}>
						<form id="form-apply-reimbursements-exchange-rates" ref={refExchangeRatesForm}>
							<ExchangeRates
								alertPosition="bottom"
								currencyBase={currency}
								exchangeRates={exchangeRates}
								onChange={handleChangeExchangeRates}
							/>
						</form>
					</Grid>
				)}

				{/* Find Transactions */}
				<Grid item xs={12}>
					<Box sx={sxBox}>
						<Box
							sx={{
								...sxBoxHeader,
								borderBottomColor: theme.palette.divider,
								borderBottomWidth: 1,
								borderBottomStyle: 'solid',
							}}>
							<Typography fontWeight={600}>{t('findTransactions')}</Typography>
						</Box>
						<Box sx={sxBoxHeading}>
							<Grid container spacing={2}>
								<Grid item xs={12} md={true}>
									<FormSearch
										paramsPreserved={['dialog', 'dialogTransaction']}
										hasButton={true}
										InputProps={{
											name: 'dialogQ',
											placeholder: t('searchByIdOrEntity'),
										}}
									/>
								</Grid>
								<Grid item xs={12} md="auto">
									<BtnFilters
										paramsPreserved={['dialog', 'dialogTransaction']}
										filters={[
											{
												hasSearchParam: true,
												label: t('withdrawalApplication'),
												name: 'dialogWithdrawalApplication',
												type: 'WITHDRAWAL_APPLICATION',
												value: filtersFromParams.withdrawalApplication,
											},
											{
												hasSearchParam: true,
												label: t('startDate'),
												name: 'dialogDateStart',
												type: 'DATE',
												value: filtersFromParams.dateStart,
											},
											{
												hasSearchParam: true,
												label: t('endDate'),
												name: 'dialogDateEnd',
												type: 'DATE',
												value: filtersFromParams.dateEnd,
											},
											{
												hasSearchParam: true,
												label: t('tags'),
												name: 'dialogTags',
												type: 'TAGS',
												value: filtersFromParams.tags,
											},
											{
												hasSearchParam: true,
												label: t('category'),
												name: 'dialogCategory',
												type: 'CATEGORY',
												value: filtersFromParams.category,
											},
											{
												hasSearchParam: true,
												label: t('component'),
												name: 'dialogComponent',
												type: 'COMPONENT',
												value: filtersFromParams.component,
											},
											{
												hasSearchParam: true,
												label: t('account'),
												name: 'dialogAccount',
												type: 'ACCOUNT',
												value: filtersFromParams.account,
											},
										]}
										PopoverProps={{
											slotProps: {
												paper: {
													sx: { maxHeight: 330 },
												},
											},
										}}
									/>
								</Grid>
							</Grid>
						</Box>
						<Table
							data={rowsAvailable}
							hasBorder={true}
							headers={headersFind}
							isLoading={isLoadingTransactions}
							sxBox={{
								...sxTableBox,
								borderLeft: 'none',
								borderRight: 'none',
							}}
						/>
						<Box sx={sxBoxFooter}>
							<Grid alignItems="center" container justifyContent="space-between">
								<Grid item>
									<Typography fontWeight={500}>
										{rowsAvailable.length} {t('transactions')}
									</Typography>
								</Grid>
								<Grid item>
									<Button onClick={handleAddAll} size="small" variant="outlined">
										{t('addAll')}
									</Button>
								</Grid>
							</Grid>
						</Box>
					</Box>
					<Typography color="brand.grey.500" component="p" sx={{ marginTop: 1 }} variant="caption">
						{asterisk} {t('table.basic.findTransactions.footer')}
					</Typography>
				</Grid>

				{/* Selected Transactions */}
				<Grid item xs={12}>
					<form id={formId} onSubmit={handleSubmit}>
						<Box sx={sxBox}>
							<Box sx={sxBoxHeader}>
								<Grid alignItems="center" container justifyContent="space-between" spacing={2}>
									<Grid item>
										<Typography fontWeight={600}>{t('selectedTransactions')}</Typography>
									</Grid>
									<Grid item>
										<Checkbox
											checked={values.hasAllConversions as boolean}
											id={`${formId}-has-all-conversions`}
											label={t('showAllConversions')}
											name="hasAllConversions"
											onChange={(e) => {
												setValues((valuesPrev) => {
													return {
														...valuesPrev,
														hasAllConversions: e.target.checked,
													};
												});
											}}
											sxBox={{ border: 'none', padding: 0 }}
										/>
									</Grid>
								</Grid>
							</Box>
							<Table
								data={rowsSelected}
								hasBorder={true}
								headers={headersSelected}
								isLoading={isLoadingRelatedTransactions}
								sxBox={{
									...sxTableBox,
									borderLeft: 'none',
									borderRight: 'none',
								}}
							/>
							<Box sx={sxBoxFooter}>
								<Grid alignItems="center" container justifyContent="space-between">
									<Grid item>
										<Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
											<Typography fontWeight={600}>
												{t('xOfY', {
													x: formatCurrency({ currency, value: totalSelected }),
													y: formatCurrency({ currency, value: totalReimbursement }),
												})}
											</Typography>
											<ChipReimbursement
												amountApplied={totalSelected}
												amountTotal={totalReimbursement}
												currency={currency}
												hasTooltip={false}
											/>
										</Box>
									</Grid>
									<Grid item>
										<Button color="error" onClick={handleClear} size="small" variant="outlined">
											{t('clearAll')}
										</Button>
									</Grid>
								</Grid>
							</Box>
						</Box>
					</form>
				</Grid>

				{/* Feedback Alert */}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
